import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authApi } from '../services/api'

interface User {
    id: number
    username: string
    full_name: string | null
    role: string
    is_active: boolean
    avatar_url?: string | null
}

interface AuthState {
    token: string | null
    user: User | null
    isAuthenticated: boolean
    isLoading: boolean
    error: string | null

    login: (username: string, password: string) => Promise<void>
    logout: () => void
    fetchUser: () => Promise<void>
    clearError: () => void
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            token: null,
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,

            login: async (username: string, password: string) => {
                set({ isLoading: true, error: null })
                try {
                    const response = await authApi.login(username, password)
                    const { access_token } = response.data

                    localStorage.setItem('token', access_token)
                    set({ token: access_token, isAuthenticated: true })

                    // Fetch user info
                    await get().fetchUser()

                    set({ isLoading: false })
                } catch (error: any) {
                    const message = error.response?.data?.detail || 'Ошибка входа'
                    set({ isLoading: false, error: message })
                    throw error
                }
            },

            logout: () => {
                localStorage.removeItem('token')
                set({
                    token: null,
                    user: null,
                    isAuthenticated: false,
                    error: null,
                })
            },

            fetchUser: async () => {
                try {
                    const response = await authApi.getMe()
                    set({ user: response.data })
                } catch (error) {
                    get().logout()
                }
            },

            clearError: () => set({ error: null }),
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({
                token: state.token,
                user: state.user,
                isAuthenticated: state.isAuthenticated,
            }),
        }
    )
)
